import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Box, TextField, MenuItem, InputAdornment, IconButton } from "@mui/material";
import AppInfoView from "@crema/core/AppInfoView";
import AppsContainer from "@crema/core/AppsContainer";
import AppsPagination from "@crema/core/AppsPagination";
import AppsContent from "@crema/core/AppsContainer/AppsContent";
import AppsHeader from "@crema/core/AppsContainer/AppsHeader";
import PolygonTable from "./PolygonTable";
import { useNavigate } from "react-router-dom";
import { getPolygon } from "redux/actions";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { AppState } from "redux/store";
import { PolygonData } from "types/models/master/MasterApp";
import { isModuleAuthorized } from "@crema/utility/Utils";

const PolygonView: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { polygon } = useSelector((state: AppState) => state.master) as {
    polygon: PolygonData[] | null;
  };

  const polygonData = polygon || [];

  const rowsPerPage = 100;

  const [searchField, setSearchField] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  const searchFields = [
    { label: "All", value: "all" },
    { label: "State", value: "stateName" },
    { label: "City", value: "city_name" },
    { label: "Area", value: "area_name" },
  ];

  useEffect(() => {
    dispatch(getPolygon("", 0, 10000)); 
  }, [dispatch]);

  const onSearchPolygon = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  const filteredPolygons = search.trim()
    ? polygonData.filter((item) => {
        const term = search.trim().toLowerCase();

        if (searchField === "all") {
          return searchFields
            .filter(f => f.value !== "all")
            .some(({ value }) => {
              const fieldValue = item[value];
              return fieldValue?.toString().toLowerCase().includes(term);
            });
        } else {
          const fieldValue = item[searchField];
          return fieldValue?.toString().toLowerCase().includes(term);
        }
      })
    : polygonData;

  const paginatedPolygons = filteredPolygons.slice(
    page * rowsPerPage,
    (page + 1) * rowsPerPage
  );

  const onPageChange = (
    _event: React.ChangeEvent<unknown> | null,
    value: number
  ) => {
    setPage(value);
  };

  const handleAddPolygonClick = () => {
    navigate("/master/manageViewPolygon/PolygonCreate");
  };

  const exportPolygonToCSV = (polygonData: PolygonData[]) => {
    if (!polygonData.length) return;

    const sanitize = (val: any) =>
      String(val ?? "").replace(/,/g, " ").replace(/\r?\n|\r/g, " ").trim();

    const maxPartners = polygonData.reduce((max, item) => {
      const partners = item?.network_partners?.split(",") || [];
      return Math.max(max, partners.length);
    }, 0);

    const headers = [
      "State",
      "City",
      "Area",
      ...Array.from({ length: maxPartners }, (_, i) => `Network Partner ${i + 1}`),
      "Status",
    ];

    const rows = polygonData.map((item) => {
      const partners = item?.network_partners?.split(",").map(sanitize) || [];
      const partnerCols = Array.from({ length: maxPartners }, (_, i) => partners[i] || "");
      return [
        sanitize(item.stateName),
        sanitize(item.city_name),
        sanitize(item.area_name),
        ...partnerCols,
        item.isActive ? "Mapped" : "Pending",
      ];
    });

    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "Polygon.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <AppsContainer
        title={
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
            <Box component="span" sx={{ fontSize: 16, fontWeight: "bold", color: "text.primary", flex: 1 }}>
              Manage Polygon
            </Box>
            <Button
              sx={{ marginRight: 2 }}
              variant="contained"
              color="secondary"
              onClick={() => exportPolygonToCSV(polygonData)}
            >
              Export
            </Button>
            {isModuleAuthorized("Add Polygon") && (
              <Button
                variant="outlined"
                color="primary"
                onClick={handleAddPolygonClick}
                sx={{ flexShrink: 0 }}
              >
                Add Polygon
              </Button>
            )}
          </Box>
        }
        fullView
      >
        <AppsHeader>
  <Box
    sx={{
      display: "flex",
      width: "100%",
      justifyContent: "flex-end",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        padding: 2,
        gap: 2,
      }}
    >
      <TextField
        select
        size="small"
        value={searchField}
        onChange={(e) => {
          setSearchField(e.target.value);
          setSearch("");
          setPage(0);
        }}
        sx={{ width: 150 }}
      >
        {searchFields.map((field) => (
          <MenuItem key={field.value} value={field.value}>
            {field.label}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        variant="outlined"
        size="small"
        value={search}
        onChange={(e) => onSearchPolygon(e.target.value)}
        placeholder="Search here"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {search ? (
                <IconButton onClick={() => onSearchPolygon("")} size="small">
                  <CloseIcon />
                </IconButton>
              ) : (
                <SearchIcon />
              )}
            </InputAdornment>
          ),
        }}
        sx={{ width: 200 }}
      />

      <Button
        variant="contained"
        color="primary"
        onClick={() => setPage(0)}
      >
        Submit
      </Button>
    </Box>
  </Box>
</AppsHeader>



        <AppsContent sx={{ paddingTop: 2.5, paddingBottom: 2.5 }}>
          <PolygonTable polygon={paginatedPolygons} />
          <AppsPagination
            rowsPerPage={rowsPerPage}
            count={filteredPolygons.length}
            page={page}
            onPageChange={onPageChange}
          />
        </AppsContent>
      </AppsContainer>
      <AppInfoView />
    </>
  );
};

export default PolygonView;
